/**
 * Categories list — дерево с drag-n-drop через react-arborist.
 *
 * Layout: 2 колонки. Левая (1/3) — дерево. Правая (2/3) — детали выбранной.
 *
 * NOTE: react-arborist должен быть в package.json. Если нет — добавить:
 *   pnpm add react-arborist
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tree, type NodeRendererProps } from "react-arborist";
import { toast } from "sonner";

import { CategoryDeleteDialog } from "@/components/categories/CategoryDeleteDialog";
import { InlineCreateDialog } from "@/components/categories/InlineCreateDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

import { CategoryEditor } from "./editor";

interface CategoryNode {
  id: string;
  name: string;
  parent_id: number | null;
  sort_order: number;
  is_visible: boolean;
  products_count: number;
  /** products_count + сумма всех потомков (для root-категорий) */
  total_products: number;
  raw: any;
  children?: CategoryNode[];
}

function buildTree(categories: any[]): CategoryNode[] {
  const map = new Map<number, CategoryNode>();
  for (const cat of categories) {
    const ru = cat.translations?.find((t: any) => t.language === "ru");
    map.set(cat.id, {
      id: String(cat.id),
      name: ru?.name ?? cat.slug,
      parent_id: cat.parent_id,
      sort_order: cat.sort_order,
      is_visible: cat.is_visible,
      products_count: cat.products_count ?? 0,
      total_products: cat.products_count ?? 0,
      raw: cat,
      children: [],
    });
  }
  const roots: CategoryNode[] = [];
  for (const node of map.values()) {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }
  // Pass 2: рекурсивно посчитать total_products = own + children
  function rollup(node: CategoryNode): number {
    let total = node.products_count;
    for (const child of node.children ?? []) {
      total += rollup(child);
    }
    node.total_products = total;
    return total;
  }
  for (const r of roots) rollup(r);
  return roots;
}

interface CreateState {
  parentId: number | null;
  parentName?: string;
}

interface DeleteState {
  id: number;
  name: string;
  parent_id: number | null;
  products_count: number;
  childrenCount: number;
}

export function CategoriesListPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createDialog, setCreateDialog] = useState<CreateState | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<DeleteState | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: async () => {
      const { data } = await api.get("/categories");
      return data as any[];
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: { id: number; parent_id: number | null; sort_order: number }[]) => {
      await api.post("/categories/reorder", { items });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      toast.success("Сохранено");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Ошибка");
    },
  });

  function handleMove({ dragIds, parentId, index }: any) {
    const items = dragIds.map((id: string) => ({
      id: Number(id),
      parent_id: parentId ? Number(parentId) : null,
      sort_order: index,
    }));
    reorderMutation.mutate(items);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">{t("nav.categories")}</h2>
          <Button
            size="sm"
            onClick={() =>
              setCreateDialog({ parentId: null })
            }
          >
            <Plus className="w-4 h-4 mr-1" />
            {t("common.create")}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8" />
            <Skeleton className="h-8 ml-4" />
            <Skeleton className="h-8 ml-4" />
            <Skeleton className="h-8" />
          </div>
        ) : (
          <div className="border rounded-md">
            <Tree
              data={buildTree(categories ?? [])}
              onMove={handleMove}
              indent={20}
              rowHeight={32}
              height={600}
              width="100%"
              openByDefault={false}
            >
              {(props) => (
                <CategoryRow
                  {...props}
                  onSelect={(id) => {
                    setSelectedId(id);
                    setIsCreating(false);
                  }}
                  onCreateSub={(id, name) =>
                    setCreateDialog({ parentId: id, parentName: name })
                  }
                  onDelete={(node) =>
                    setDeleteDialog({
                      id: Number(node.id),
                      name: node.name,
                      parent_id: node.parent_id,
                      products_count: node.products_count,
                      childrenCount: node.children?.length ?? 0,
                    })
                  }
                />
              )}
            </Tree>
          </div>
        )}
      </Card>

      <InlineCreateDialog
        open={createDialog != null}
        onClose={() => setCreateDialog(null)}
        parentId={createDialog?.parentId ?? null}
        parentName={createDialog?.parentName}
        onCreated={(id) => {
          setSelectedId(id);
          setIsCreating(false);
          queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
        }}
      />
      {deleteDialog && (
        <CategoryDeleteDialog
          open={true}
          onClose={() => setDeleteDialog(null)}
          category={{
            id: deleteDialog.id,
            name: deleteDialog.name,
            parent_id: deleteDialog.parent_id,
            products_count: deleteDialog.products_count,
          }}
          childrenCount={deleteDialog.childrenCount}
          onDeleted={() => {
            if (selectedId === deleteDialog.id) setSelectedId(null);
            queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
          }}
        />
      )}

      <div className="lg:col-span-2">
        {isCreating ? (
          <CategoryEditor
            mode="create"
            onSaved={() => {
              setIsCreating(false);
              queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
            }}
          />
        ) : selectedId ? (
          <CategoryEditor
            mode="edit"
            categoryId={selectedId}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
            }}
          />
        ) : (
          <Card className="p-12 text-center text-muted-foreground">
            Выберите категорию слева или создайте новую
          </Card>
        )}
      </div>
    </div>
  );
}

interface CategoryRowProps extends NodeRendererProps<CategoryNode> {
  onSelect: (id: number) => void;
  onCreateSub: (parentId: number, parentName: string) => void;
  onDelete: (node: CategoryNode) => void;
}

function CategoryRow({
  node,
  style,
  dragHandle,
  onSelect,
  onCreateSub,
  onDelete,
}: CategoryRowProps): JSX.Element {
  const hasChildren = node.children && node.children.length > 0;
  const own = node.data.products_count;
  const total = node.data.total_products;
  const countLabel = hasChildren
    ? own > 0
      ? `${total} (${own} свой)`
      : `${total}`
    : `${own}`;
  const isEmpty = total === 0;
  return (
    <div
      style={style}
      ref={dragHandle}
      onClick={() => onSelect(Number(node.id))}
      className={`group flex items-center justify-between gap-2 px-2 py-1 cursor-pointer hover:bg-accent rounded-sm text-sm ${
        node.isSelected ? "bg-accent" : ""
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center">
        {hasChildren && (
          <span
            className="mr-1 text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              node.toggle();
            }}
          >
            {node.isOpen ? "▼" : "▶"}
          </span>
        )}
        <span
          className={`truncate ${node.data.is_visible ? "" : "line-through opacity-60"}`}
        >
          {node.data.name}
        </span>
      </div>

      <span
        className={`shrink-0 rounded px-1.5 text-xs tabular-nums ${
          isEmpty
            ? "bg-destructive/15 text-destructive"
            : "bg-muted text-muted-foreground"
        }`}
        title={
          isEmpty
            ? "В категории нет товаров — можно удалить"
            : `Товаров в категории: ${total}`
        }
      >
        {countLabel}
      </span>

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
        <button
          type="button"
          title="Добавить подкатегорию"
          className="rounded hover:bg-background p-1 text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onCreateSub(Number(node.id), node.data.name);
          }}
        >
          <Plus className="w-3 h-3" />
        </button>
        <button
          type="button"
          title="Удалить"
          className="rounded hover:bg-destructive/10 p-1 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(node.data);
          }}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
